import { useEffect, useRef, useState } from 'react';
import { Trash2, Mic, SendHorizontal, Paperclip, Smile, X } from 'lucide-react';
import { useVoiceRecorder } from '@shared/hooks/use-voice-recorder';
import { EmojiPicker } from './emoji-picker';

type Props = {
  disabled?: boolean;
  onSend: (text: string, file: File | null, metadata?: Record<string, unknown>) => void;
};

export function Composer({
  disabled,
  onSend,
}: Props) {
  const [text, setText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const {
    isRecording,
    recordingDuration,
    waveformLevels,
    error: recordingError,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecorder();

  async function handleSendVoice() {
    const result = await stopRecording();
    if (!result) return;
    onSend('', result.file, {
      isVoiceNote: true,
      durationSeconds: result.durationSeconds,
    });
  }

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 144)}px`;
  }, [text]);

  useEffect(() => {
    if (!disabled) {
      taRef.current?.focus();
    }
  }, [disabled]);

  function submit() {
    const body = text.trim();
    if (!body && !selectedFile) return;
    onSend(body, selectedFile);
    setText('');
    setSelectedFile(null);
  }

  function insertNewline() {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = ta.value;
    const nextText = value.substring(0, start) + '\n' + value.substring(end);
    setText(nextText);
    setTimeout(() => {
      if (taRef.current) {
        taRef.current.selectionStart = taRef.current.selectionEnd = start + 1;
      }
    }, 0);
  }

  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.key === 'Control' && e.altKey) || (e.key === 'Alt' && e.ctrlKey)) {
      e.preventDefault();
      insertNewline();
    } else if (e.key === 'Enter') {
      if (e.ctrlKey && e.altKey) {
        e.preventDefault();
      } else if (e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
    e.target.value = '';
  }

  return (
    <div className="bg-[#efeae2] pb-2 sm:pb-3 px-2 sm:px-3 shrink-0">
      {selectedFile ? (
        <div className="mx-1 mt-1 flex items-center gap-2 rounded-t-xl bg-white border-l-4 border-slate-700 px-3.5 py-2.5 text-xs shadow-sm">
          <div className="flex-1 truncate flex items-center gap-1.5">
            <span className="font-semibold text-slate-800">Attachment:</span>
            <span className="text-gray-600 truncate">{selectedFile.name}</span>
            <span className="text-[10px] text-gray-400">
              ({(selectedFile.size / 1024).toFixed(1)} KB)
            </span>
          </div>
          <button
            type="button"
            onClick={() => setSelectedFile(null)}
            className="text-gray-500 hover:text-gray-700 p-0.5 rounded"
            title="Remove attachment"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      {recordingError ? (
        <div className="flex items-center justify-between bg-red-50 px-4 py-2 text-xs text-red-700 border border-red-200 rounded-lg mb-2 shadow-2xs">
          <span>{recordingError}</span>
          <button
            type="button"
            onClick={cancelRecording}
            className="text-red-500 hover:text-red-700 p-0.5 rounded"
            title="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      <div className="relative flex items-end gap-2 mt-2">
        {isRecording ? (
          /* Voice recording active container */
          <div className="flex-1 min-w-0 flex items-center justify-between min-h-[46px] bg-red-50/80 border border-red-200/90 rounded-[24px] px-3.5 py-1.5 shadow-2xs animate-in fade-in duration-150">
            {/* Live recording indicator & timer */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
              </span>
              <span className="text-xs font-mono font-semibold text-red-700 tracking-tight">
                {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
              </span>
            </div>

            {/* Live dynamic waveform equalizer bars */}
            <div className="flex-1 max-w-[200px] sm:max-w-[280px] mx-2.5 h-6 flex items-center justify-center gap-[2.5px] overflow-hidden">
              {waveformLevels.length === 0 ? (
                <span className="text-[11px] text-gray-400 italic">Listening…</span>
              ) : (
                waveformLevels.map((lvl, i) => (
                  <span
                    key={i}
                    className="w-[2.5px] sm:w-[3px] rounded-full bg-red-500 transition-all duration-75"
                    style={{
                      height: `${Math.max(4, Math.round(lvl * 24))}px`,
                    }}
                  />
                ))
              )}
            </div>

            {/* Cancel (Trash) button using Lucide Trash2 */}
            <button
              type="button"
              onClick={cancelRecording}
              className="p-1.5 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-100/70 transition-colors shrink-0"
              title="Discard recording"
              aria-label="Discard recording"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        ) : (
          /* White input pill */
          <div className="flex-1 min-w-0 flex items-end bg-white rounded-[24px] border border-gray-200/80 shadow-sm px-1.5 py-1 min-h-[40px] relative">
            <button
              type="button"
              disabled={disabled}
              onClick={() => setShowEmoji((v) => !v)}
              className="p-2 text-gray-500 hover:text-gray-600 shrink-0 rounded-full hover:bg-gray-100/50 disabled:opacity-50"
              aria-label="Add emoji"
            >
              <Smile className="h-5 w-5" />
            </button>

            <button
              type="button"
              disabled={disabled}
              onClick={() => fileRef.current?.click()}
              className="p-2 text-gray-500 hover:text-gray-600 shrink-0 rounded-full hover:bg-gray-100/50 disabled:opacity-50"
              aria-label="Attach file"
            >
              <Paperclip className="h-5 w-5" />
            </button>
            <input ref={fileRef} type="file" hidden onChange={onFileChange} />

            {showEmoji ? (
              <EmojiPicker
                onPick={(e) => {
                  setText((t) => t + e);
                  taRef.current?.focus();
                }}
                onClose={() => setShowEmoji(false)}
              />
            ) : null}

            <textarea
              ref={taRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder={disabled ? 'This chat is closed' : 'Type a message as Admin…'}
              disabled={disabled}
              className="flex-1 min-w-0 resize-none border-0 bg-transparent py-2 px-2 focus:ring-0 focus:outline-none text-[15px] max-h-[120px] leading-5 text-gray-900 placeholder:text-gray-400 disabled:text-gray-400"
              style={{ outline: 'none', boxShadow: 'none' }}
            />
          </div>
        )}

        {/* Circular Action Button: Voice Send vs Microphone vs Message Send */}
        {isRecording ? (
          <button
            type="button"
            onClick={handleSendVoice}
            className="flex items-center justify-center w-11 h-11 rounded-full bg-[#008665] text-white hover:bg-[#006f53] active:bg-[#005c45] transition-all transform hover:scale-105 shadow-sm shrink-0"
            aria-label="Send voice message"
            title="Send voice message"
          >
            <SendHorizontal className="h-5 w-5 ml-0.5" />
          </button>
        ) : !text.trim() && !selectedFile ? (
          <button
            type="button"
            onClick={startRecording}
            disabled={disabled}
            className="flex items-center justify-center w-11 h-11 rounded-full bg-[#008665] text-white hover:bg-[#006f53] active:bg-[#005c45] transition-all transform hover:scale-105 shadow-sm shrink-0 disabled:opacity-50 disabled:bg-gray-300 disabled:text-gray-500"
            aria-label="Record voice message"
            title="Record voice message"
          >
            <Mic className="h-5 w-5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={disabled}
            className="flex items-center justify-center w-11 h-11 rounded-full bg-[#008665] text-white hover:bg-[#006f53] active:bg-[#005c45] transition-colors shadow-sm shrink-0 disabled:opacity-50 disabled:bg-gray-300 disabled:text-gray-500"
            aria-label="Send message"
          >
            <SendHorizontal className="h-5 w-5 ml-0.5" />
          </button>
        )}
      </div>
    </div>
  );
}
